/**
 * useMyModules — shared React Query hook dla `GET /api/my/modules` (F7b T7b.7).
 *
 * Response shape (F7b T7b.7 rename — breaking, bez consumerów F5 T5.10 skeleton):
 *   `{catalog: CatalogModule[], subscriptions: ActiveModule[]}`
 *
 * Konsumenci:
 *  - `Sidebar` (tenant scope) — merge catalog + subscriptions do menu (active = link,
 *    inactive = disabled+tooltip).
 *  - `ModuleGuard` — wrapper sprawdza czy dany `moduleCode` jest w `subscriptions`.
 *
 * Cache TTL 60s (default z `createQueryClient()` w `lib/query.ts`) — wystarczy,
 * bo backend też cachuje 60s. `invalidate` ręczne gdy user zmieni subskrypcję
 * (TBD F8 payment flow).
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface CatalogModule {
  code: string;
  name: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  isCore: boolean;
  sortOrder: number | null;
  /**
   * T20.3 — obszar (moduleAreas) do którego moduł jest przypisany. `null` dla starych
   * hardkodowanych modułów bez obszaru. Płaska lista w sidebarze pomija moduły z obszarem
   * (renderuje je sekcja obszaru z `/api/my/installed-modules`).
   */
  areaId?: string | null;
  /**
   * T8.1b — `true` gdy moduł jest wycofany z katalogu (`gcrecord IS NOT NULL`)
   * ale tenant ma aktywną subskrypcję, więc backend wstrzyknął go do listy. Wpisy
   * z aktywnego katalogu mają `false`/undefined.
   */
  isRetired?: boolean;
  /**
   * Krok 3 kreatora modułów — `true` dla szablonów z kreatora. Płaska lista modułów
   * w sidebarze je pomija (ich rodzaje danych pokazuje sekcja obszarów z gatingiem
   * po subskrypcji). Dla hardkodowanych modułów (CRM, Symfonia) `false`.
   */
  isTemplateOnly?: boolean;
}

export interface SubscribedModule {
  code: string;
  name: string;
  status: 'active' | 'trialing';
  trialEndsAt: string | null;
  isCore: boolean;
}

export interface MyModulesResponse {
  catalog: CatalogModule[];
  subscriptions: SubscribedModule[];
}

export const MY_MODULES_QUERY_KEY = ['tenant', 'my-modules'] as const;

export function useMyModules(opts?: { enabled?: boolean }) {
  return useQuery<MyModulesResponse>({
    queryKey: MY_MODULES_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<MyModulesResponse>('/my/modules');
      return res.data;
    },
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
  });
}
